import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Segmented, Spinner } from '@/components/common';
import { useResource, useStoredState } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import { useAuth } from '../auth';
import { ConsentMethodsPanel } from '../consents/components/ConsentMethodsPanel';
import { settingsApi } from './api';
import { AuditLog } from './components/AuditLog';
import { ClinicDetails } from './components/ClinicDetails';
import { UsersPanel } from './components/UsersPanel';
import { WorkingHours } from './components/WorkingHours';

type Tab = 'clinic' | 'hours' | 'users' | 'consents' | 'audit';

/**
 * Clinic details, working hours and calendar, users, the invasive methods of
 * consents, and the audit log. In support mode the last two stay closed.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stored, setTab] = useStoredState<Tab>('dentlog.settings.tab', 'clinic');
  const clinic = useResource(() => settingsApi.clinic(), 'clinic');
  const tabs: { value: Tab; label: string }[] = [
    { value: 'clinic', label: t('settings.tabs.clinic') },
    { value: 'hours', label: t('settings.tabs.hours') },
    { value: 'users', label: t('settings.tabs.users') },
    // Both name clinical content the support mode stays out of.
    ...(user?.support
      ? []
      : [
          { value: 'consents' as const, label: t('settings.tabs.consents') },
          { value: 'audit' as const, label: t('settings.tabs.audit') },
        ]),
  ];
  const tab = tabs.some((x) => x.value === stored) ? stored : 'clinic';

  const clinicTab = (render: (c: NonNullable<typeof clinic.data>) => ReactNode) => {
    if (clinic.error) return <Banner tone="error">{errorMessage(clinic.error)}</Banner>;
    if (!clinic.data) return <Spinner />;
    return render(clinic.data);
  };

  return (
    <div className="stack">
      <div className="page-head">
        <div className="page-head__title">
          <h1>{t('settings.title')}</h1>
        </div>
      </div>
      <Segmented label={t('settings.title')} value={tab} onChange={setTab} options={tabs} />
      {tab === 'clinic' &&
        clinicTab((c) => <ClinicDetails key={c.id} clinic={c} onChange={clinic.setData} />)}
      {tab === 'hours' &&
        clinicTab((c) => <WorkingHours key={c.id} clinic={c} onChange={clinic.setData} />)}
      {tab === 'users' && <UsersPanel />}
      {tab === 'consents' && <ConsentMethodsPanel />}
      {tab === 'audit' && <AuditLog />}
    </div>
  );
}
